import styles from './index.module.css'

import { Flex, Heading, Table, Text } from '@radix-ui/themes'
import React from 'react'

import type { RightsData, TerritorySplit } from '@disco/types/types/rights-data'

import { Box } from 'components/ds'

const renderNonZero = (
  value: string | number | undefined,
  prefix = '',
  suffix = '',
) => {
  if (value != 0 && value !== '' && value != null) {
    return `${prefix}${value}${suffix}`
  }
  return ''
}

const writerKey = (writer: { name: string; ipi: string | number }) => {
  const ipi = writer.ipi
  if (ipi && ipi !== '0' && ipi !== 0 && ipi !== '') {
    return String(ipi)
  }
  return `unknown-${writer.name}`
}

export function RightsDataTable({
  territory,
  splits,
  allWriters,
  master,
}: {
  territory: { code: string; name: string }
  splits: TerritorySplit[]
  allWriters: RightsData['all_writers']
  master: RightsData['master']
}) {
  const representedKeys = new Set<string>()
  const representedNames = new Set<string>()
  splits.forEach(split => {
    split.represented_writers.forEach(writer => {
      representedKeys.add(writerKey(writer))
      representedNames.add(writer.name)
    })
  })

  const splitTotal = splits.reduce((acc, s) => acc + s.split, 0)

  const otherWriters = allWriters.filter(name => !representedNames.has(name))

  const publishingColumns = [
    'Publisher',
    `Writers${renderNonZero(representedKeys.size, ' • ')}`,
    `Split${renderNonZero(splitTotal, ' • ', '%')}`,
    'Admin',
  ].map(column => (
    <Table.ColumnHeaderCell key={column}>
      <Text weight="medium">{column}</Text>
    </Table.ColumnHeaderCell>
  ))

  const publishingRows = splits.map((split, ind) => (
    <Table.Row key={ind}>
      <Table.RowHeaderCell>
        {split.publisher && (
          <Flex direction="column">
            <Text size="1">{split.publisher.name}</Text>
            <Text size="1">
              {renderNonZero(split.publisher.ipi, '(IPI: ', ')')}
            </Text>
          </Flex>
        )}
      </Table.RowHeaderCell>
      <Table.Cell>
        {split.represented_writers.map((writer, writerInd) => (
          <Flex direction="column" key={`${writerKey(writer)}-${writerInd}`}>
            <Text size="1">{writer.name}</Text>
            <Text size="1">{renderNonZero(writer.ipi, '(IPI: ', ')')}</Text>
          </Flex>
        ))}
      </Table.Cell>
      <Table.Cell>
        <Text size="1">{split.split}%</Text>
      </Table.Cell>
      <Table.Cell>
        {split.admin && (
          <Flex direction="column">
            <Text size="1">{split.admin.name}</Text>
            {split.admin.parent && <Text size="1">{split.admin.parent}</Text>}
            <Text size="1">
              {renderNonZero(split.admin.ipi, '(IPI: ', ')')}
            </Text>
          </Flex>
        )}
      </Table.Cell>
    </Table.Row>
  ))

  return (
    <Box mb="2">
      <Heading size="2">
        Publishing: {territory.code} - {territory.name}
      </Heading>
      <Table.Root size="1" variant="surface" className={styles.publishingTable}>
        <Table.Header>
          <Table.Row>{publishingColumns}</Table.Row>
        </Table.Header>
        <Table.Body>
          {publishingRows}
          {!!otherWriters.length && (
            <>
              <Table.Header className={styles.headerChanges}>
                <Table.Row>
                  <Table.ColumnHeaderCell colSpan={4}>
                    <Text weight="medium">Other Writers</Text>
                  </Table.ColumnHeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Row>
                <Table.Cell colSpan={4}>
                  <Text size="1">{otherWriters.join(', ')}</Text>
                </Table.Cell>
              </Table.Row>
            </>
          )}
        </Table.Body>
      </Table.Root>
      {(master.label || master.isrc) && (
        <>
          <Heading size="2">
            Recording: {territory.code} - {territory.name}
          </Heading>

          <Table.Root variant="surface" size="1">
            <Table.Header>
              <Table.ColumnHeaderCell>
                <Text weight="medium">Label</Text>
              </Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>
                <Text weight="medium">ISRC</Text>
              </Table.ColumnHeaderCell>
            </Table.Header>
            <Table.Body>
              <Table.Row>
                <Table.RowHeaderCell>
                  <Text size="1">{master.label}</Text>
                </Table.RowHeaderCell>
                <Table.Cell>
                  <Text size="1">{master.isrc}</Text>
                </Table.Cell>
              </Table.Row>
            </Table.Body>
          </Table.Root>
        </>
      )}
    </Box>
  )
}
